// Main ordering page component
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useIsMobile, useProducts, useDiscount } from '../hooks';
import { useCartStore } from '../store/cartStore';
import { storageService } from '../services/storage';
import { ProductCategory, OpeningHoursStatus } from '../types';
import { CATEGORY_METADATA, CATEGORIES } from '../shared/categories';
import { isProductVisibleOnMenu } from '../shared/productUtils';
import { apiService } from '../services/api';
import ProductCard from './ProductCard';
import CategoryFilter from './CategoryFilter';
import Cart from './Cart';
import CartSummary from './CartSummary';
import { StockAlert } from './StockAlert';
import Modal from './Modal';
import DelayAlertModal from './DelayAlertModal';
import WelcomeDiscountModal from './WelcomeDiscountModal';
import TemporaryClosureModal from './TemporaryClosureModal';
import MainPageLayout from './MainPageLayout';
import HygieneRatingBanner from './HygieneRatingBanner';
import HygieneRatingModal from './HygieneRatingModal';
import { TEMPORARY_CLOSURE_ENABLED } from '../shared/temporaryClosure';
import './OrderPage.css';

const OrderPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [selectedCategory, setSelectedCategory] = useState<ProductCategory | 'all'>('all');
    // Fetch only available products for customer-facing page (availableOnly = true)
    const { data: allProducts, error, refetch } = useProducts('all', true);
    const { initializeFromStorage, validateAndCorrectCart, syncCartPrices } = useCartStore();
    const { applyCode } = useDiscount();
    const isMobile = useIsMobile();

    // Auto-discount configuration
    const AUTO_DISCOUNT_CODE = 'SPRING';
    // Legacy code that should be replaced by the new auto-discount
    const LEGACY_DISCOUNT_CODE = 'NEW-YEAR';

    // Filter products based on selected category — show all visible products (including sold-out)
    const products = selectedCategory === 'all'
        ? allProducts?.filter(isProductVisibleOnMenu)
        : allProducts?.filter(p => p.category === selectedCategory && isProductVisibleOnMenu(p));

    const cartSectionRef = useRef<HTMLDivElement>(null);
    const productsSectionRef = useRef<HTMLDivElement>(null);
    const [isCartModalOpen, setCartModalOpen] = useState(false);
    const [isCheckoutModalOpen, setCheckoutModalOpen] = useState(false);

    // Delay alert state
    const [showDelayAlert, setShowDelayAlert] = useState(false);

    // Welcome discount state
    const [showWelcomeDiscount, setShowWelcomeDiscount] = useState(false);

    // Hygiene rating modal state
    const [showHygieneModal, setShowHygieneModal] = useState(false);
    const [showTemporaryClosure, setShowTemporaryClosure] = useState(TEMPORARY_CLOSURE_ENABLED);

    // Track if we've already checked opening hours to prevent repeated checks
    const hasCheckedOpeningHours = useRef(false);

    // Initialize cart from storage on component mount and validate against current stock
    useEffect(() => {
        initializeFromStorage();

        // Validate cart against current stock levels — removes sold-out items
        validateAndCorrectCart();

        // Reset document title to default when returning to order page
        const metaDescription = document.querySelector('meta[name="description"]');
        if (metaDescription) {
            metaDescription.setAttribute('content', 'DeliFeed - Fresh Mediterranean food delivered from Upper Cambourne. Order online for fast delivery.');
        }
    }, [initializeFromStorage, validateAndCorrectCart]);

    // Sync cart prices when products are loaded — corrects stale prices from previous sessions
    useEffect(() => {
        if (allProducts && allProducts.length > 0) {
            syncCartPrices(allProducts);
        }
    }, [allProducts, syncCartPrices]);

    // Fetch opening hours and check for delay alert + show hours modal if closed
    useEffect(() => {
        if (TEMPORARY_CLOSURE_ENABLED) {
            hasCheckedOpeningHours.current = true;
            return;
        }

        // Only run this check once per page mount
        if (hasCheckedOpeningHours.current) {
            return;
        }

        const fetchOpeningHours = async () => {
            try {
                const [hours, statusResponse] = await Promise.all([
                    apiService.getOpeningHours(),
                    fetch('/api/opening-hours/status')
                ]);

                // Show delay alert on every page load if active
                if (hours.isDelayActive) {
                    setShowDelayAlert(true);
                }

                // Check if store is closed and show working hours modal
                // BUT only if user doesn't already have a modal open (via location.state.fromPath)
                if (statusResponse.ok) {
                    const status = await statusResponse.json() as OpeningHoursStatus;
                    if (!status.isOpen && !location.state?.fromPath) {
                        // Trigger the working hours modal by navigating with state
                        navigate('/', { state: { fromPath: '/working-hours' }, replace: true });
                    }
                }

                // Mark that we've checked
                hasCheckedOpeningHours.current = true;
            } catch (error) {
                console.error('Failed to fetch opening hours:', error);
            }
        };

        fetchOpeningHours();
    }, [navigate, location.state]);

    // Auto-apply discount code ONLY on initial page load if no discount exists in storage
    useEffect(() => {
        const autoApplyDiscount = async () => {
            if (TEMPORARY_CLOSURE_ENABLED) {
                return;
            }

            // Check if auto-discount code is configured
            if (!AUTO_DISCOUNT_CODE || AUTO_DISCOUNT_CODE.trim() === '') {
                return;
            }

            // Check localStorage directly - don't depend on cart state
            const storedCart = storageService.getCart();

            console.log('[AUTO-DISCOUNT] Stored cart discount:', storedCart?.discount);

            // Don't apply if user already has ANY discount code saved,
            // EXCEPT if they have the legacy NEW-YEAR code - replace it with the new SPRING code
            if (storedCart?.discount) {
                const existingCode = storedCart.discount.code?.toUpperCase();
                if (existingCode !== LEGACY_DISCOUNT_CODE) {
                    console.log('[AUTO-DISCOUNT] User already has discount in storage, skipping');
                    return;
                }
                console.log('[AUTO-DISCOUNT] User has legacy', LEGACY_DISCOUNT_CODE, '- replacing with', AUTO_DISCOUNT_CODE);
            }

            console.log('[AUTO-DISCOUNT] No discount found, attempting to apply', AUTO_DISCOUNT_CODE);

            try {
                await applyCode(AUTO_DISCOUNT_CODE);
                console.log('[AUTO-DISCOUNT] Successfully applied, showing welcome modal');
                setShowWelcomeDiscount(true);
            } catch (error) {
                // Silent skip - discount code might be expired or invalid
                console.log('[AUTO-DISCOUNT] Failed to apply:', error);
            }
        };

        autoApplyDiscount();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Only run once on mount

    const handleCloseDelayAlert = () => {
        setShowDelayAlert(false);
    };

    const handleCloseWelcomeDiscount = () => {
        setShowWelcomeDiscount(false);
    };

    // // Initialize Apple Pay eligibility check early (before checkout)
    // // This populates the global store so CheckoutForm can read it immediately
    // console.log('[OrderPage] Initializing Apple Pay eligibility check...');
    // useApplePayEligibility({
    //     currency: 'GBP',
    //     enabled: true,
    //     allowCrossDeviceFallback: false
    // });
    // console.log('[OrderPage] useApplePayEligibility hook called');

    // Close cart/checkout modals when breakpoint changes
    useEffect(() => {
        if (isMobile) {
            setCheckoutModalOpen(false);
        } else {
            setCartModalOpen(false);
        }
    }, [isMobile]);



    const handleGoToCart = useCallback(() => {
        if (isMobile) {
            setCartModalOpen(true);
            return;
        }

        if (cartSectionRef.current) {
            cartSectionRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [isMobile]);

    const handleCloseCartModal = useCallback(() => {
        setCartModalOpen(false);
    }, []);

    const handleOpenCheckoutModal = useCallback(() => {
        if (TEMPORARY_CLOSURE_ENABLED) {
            setShowTemporaryClosure(true);
            return;
        }

        setCheckoutModalOpen(true);
    }, []);

    const handleCloseCheckoutModal = useCallback(() => {
        setCheckoutModalOpen(false);
    }, []);

    // if (loading) {
    //     return (
    //         <div className="order-page">
    //             <div className="loading-container">
    //                 <div className="loading-spinner"></div>
    //                 <p>Loading delicious menu...</p>
    //             </div>
    //         </div>
    //     );
    // }

    if (error) {
        return (
            <div className="order-page">
                <div className="error-container">
                    <h2>Oops! Something went wrong</h2>
                    <p>{error}</p>
                    <button onClick={refetch} className="retry-button">
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    return (
        <MainPageLayout>
            <div className="order-page">
                <StockAlert position="fixed" />

                <main className="order-main">
                    <div className="main-container">
                        <div className="products-container" ref={productsSectionRef}>
                            <HygieneRatingBanner />
                            <HygieneRatingModal
                                isOpen={showHygieneModal}
                                onClose={() => setShowHygieneModal(false)}
                            />
                            <CategoryFilter
                                selectedCategory={selectedCategory}
                                onCategoryChange={setSelectedCategory}
                                onScrollToProducts={() => {
                                    if (productsSectionRef.current) {
                                        const yOffset = -20; // Small offset from top
                                        const y = productsSectionRef.current.getBoundingClientRect().top + window.pageYOffset + yOffset;
                                        window.scrollTo({ top: y, behavior: 'smooth' });
                                    }
                                }}
                                availableCategories={
                                    allProducts
                                        ? (() => {
                                            // Get unique categories from VISIBLE products (includes sold-out)
                                            const availableProductCategories = new Set(
                                                allProducts
                                                    .filter(isProductVisibleOnMenu)
                                                    .map(p => p.category)
                                                    .filter(Boolean)
                                            );

                                            // Filter CATEGORIES array to only include categories that have available products
                                            return CATEGORIES
                                                .map(cat => cat.value)
                                                .filter(cat => availableProductCategories.has(cat));
                                        })()
                                        : []
                                }
                            />

                            {/* Item count display */}
                            {products && products.length > 0 && (
                                <div className="items-count">
                                    {products.length} {products.length === 1 ? 'item' : 'items'}
                                </div>
                            )}

                            {selectedCategory === 'all' ? (
                                // Group products by category when "All Items" is selected
                                products && products.length > 0 ? (
                                    (() => {
                                        // Group products by category
                                        const categoryGroups = products.reduce((groups, product) => {
                                            const category = product.category as ProductCategory;
                                            if (!groups[category]) {
                                                groups[category] = [];
                                            }
                                            groups[category].push(product);
                                            return groups;
                                        }, {} as Record<ProductCategory, typeof products>);

                                        // Sort products within each category
                                        (Object.keys(categoryGroups) as ProductCategory[]).forEach((category) => {
                                            categoryGroups[category].sort((a, b) => {
                                                // First, sort by sortOrder (if defined)
                                                const aSortOrder = a.sortOrder ?? 999999;
                                                const bSortOrder = b.sortOrder ?? 999999;

                                                if (aSortOrder !== bSortOrder) {
                                                    return aSortOrder - bSortOrder;
                                                }

                                                // Then by stock status
                                                const aStock = a.availableStock ?? Infinity;
                                                const bStock = b.availableStock ?? Infinity;

                                                const aOutOfStock = aStock !== Infinity && aStock <= 0;
                                                const bOutOfStock = bStock !== Infinity && bStock <= 0;

                                                if (aOutOfStock && !bOutOfStock) return 1;
                                                if (!aOutOfStock && bOutOfStock) return -1;

                                                if (aStock !== bStock) {
                                                    return bStock - aStock;
                                                }

                                                // Finally by name
                                                return a.name.localeCompare(b.name);
                                            });
                                        });

                                        return (
                                            <>
                                                {CATEGORIES.map((categoryMeta) => {
                                                    const category = categoryMeta.value;
                                                    // Skip categories that have no products
                                                    if (!categoryGroups[category] || categoryGroups[category].length === 0) {
                                                        return null;
                                                    }

                                                    return (
                                                        <section key={category} className="products-section">
                                                            <h2 className="section-title">
                                                                {categoryMeta.label}
                                                            </h2>
                                                            <div className={`products-grid ${categoryGroups[category].length === 1 ? 'products-grid-single' : ''}`}>
                                                                {categoryGroups[category].map((product) => (
                                                                    <ProductCard
                                                                        key={product.id}
                                                                        product={product}
                                                                        layout={categoryGroups[category].length === 1 ? 'horizontal' : 'grid'}
                                                                    />
                                                                ))}
                                                            </div>
                                                        </section>
                                                    );
                                                })}
                                            </>
                                        );
                                    })()
                                ) : (
                                    <div className="empty-products">
                                        <p>No items available.</p>
                                    </div>
                                )
                            ) : (
                                // Show filtered products for specific category
                                <section className="products-section">
                                    <h2 className="section-title">
                                        {CATEGORY_METADATA[selectedCategory].label}
                                    </h2>

                                    {products && products.length > 0 ? (
                                        <div className={`products-grid ${products.length === 1 ? 'products-grid-single' : ''}`}>
                                            {products
                                                .sort((a, b) => {
                                                    // First, sort by sortOrder (if defined)
                                                    const aSortOrder = a.sortOrder ?? 999999;
                                                    const bSortOrder = b.sortOrder ?? 999999;

                                                    if (aSortOrder !== bSortOrder) {
                                                        return aSortOrder - bSortOrder;
                                                    }

                                                    // Then by stock status
                                                    const aStock = a.availableStock ?? Infinity;
                                                    const bStock = b.availableStock ?? Infinity;

                                                    const aOutOfStock = aStock !== Infinity && aStock <= 0;
                                                    const bOutOfStock = bStock !== Infinity && bStock <= 0;

                                                    if (aOutOfStock && !bOutOfStock) return 1;
                                                    if (!aOutOfStock && bOutOfStock) return -1;

                                                    if (aStock !== bStock) {
                                                        return bStock - aStock;
                                                    }

                                                    // Finally by name
                                                    return a.name.localeCompare(b.name);
                                                })
                                                .map((product) => (
                                                    <ProductCard
                                                        key={product.id}
                                                        product={product}
                                                        layout={products.length === 1 ? 'horizontal' : 'grid'}
                                                    />
                                                ))}
                                        </div>
                                    ) : (
                                        <div className="empty-products">
                                            <p>No items found in this category.</p>
                                        </div>
                                    )}
                                </section>
                            )}
                        </div>

                        {!isMobile && (
                            <div
                                className="desktop-cart-container desktop-cart-sticky"
                                ref={cartSectionRef}
                            >
                                <Cart
                                    className="desktop-cart"
                                    checkoutMode="external"
                                    onRequestCheckout={handleOpenCheckoutModal}
                                />
                            </div>
                        )}
                    </div>
                </main>

                <CartSummary onGoToCart={handleGoToCart} />

                <Modal
                    isOpen={isCartModalOpen && isMobile}
                    onClose={handleCloseCartModal}
                    showCloseButton={false}
                    ariaLabelledBy="cart-modal-title"
                >
                    <Cart
                        variant="modal"
                        onClose={handleCloseCartModal}
                    />
                </Modal>

                <Modal
                    isOpen={isCheckoutModalOpen && !isMobile}
                    onClose={handleCloseCheckoutModal}
                    showCloseButton={false}
                    ariaLabelledBy="checkout-modal-title"
                >
                    <Cart
                        variant="modal"
                        onClose={handleCloseCheckoutModal}
                        initialView="checkout"
                    />
                </Modal>

                <DelayAlertModal
                    isOpen={!TEMPORARY_CLOSURE_ENABLED && showDelayAlert}
                    onClose={handleCloseDelayAlert}
                />

                <WelcomeDiscountModal
                    isOpen={!TEMPORARY_CLOSURE_ENABLED && showWelcomeDiscount}
                    onClose={handleCloseWelcomeDiscount}
                    discountCode="SPRING"
                    discountPercentage={25}
                />

                <TemporaryClosureModal
                    isOpen={showTemporaryClosure}
                    onClose={() => setShowTemporaryClosure(false)}
                />
            </div>
        </MainPageLayout>
    );
};

export default OrderPage;
